import { arcadeApiClient } from 'apiClients'
import { get, find as lodashFind, reduce as lodashReduce, values } from 'lodash'
import { DateTime } from 'luxon'
import { CannedDateRange } from 'ui/components/CbTableV2'
import {
  CacheKeyGenerator,
  Column,
  FilterType,
  ResultSetFromPromiseResolution,
  TableConfig,
} from './types'
import { hookDefaults } from './useTable'
import { NO_FILTER } from './useTable'

/* Translate filters { [key: string]: string } to a query string
   - if value is a custom date range, translate to YYYY/M/D format
*/
const filterStringFromConfig = (
  config: TableConfig,
  columnDefs: Column[],
): string => {
  return lodashReduce(
    config.filters,
    (filterParamString, value, columnId) => {
      if (value === NO_FILTER) return filterParamString
      if (Array.isArray(value) && value.length === 0) return filterParamString

      const column = columnDefs.find(c => c.id === columnId)
      const columnType = get(
        column,
        'filterConfig.type',
        FilterType.SingleSelection,
      )

      const queryKey = get(column, 'queryParamId', columnId)

      // If the value is a custom date range, transform into an Ruby-friendly format
      if (
        columnType === FilterType.DateRange &&
        !values(CannedDateRange).includes(value as CannedDateRange)
      ) {
        const translated = (value as string)
          .split(/[-~]/)
          .map(d =>
            DateTime.fromFormat(d.trim(), 'M/d/yyyy').toFormat('yyyy/MM/dd'),
          )
          .join('-')
        return `${filterParamString}&${queryKey}=${translated}`
      }

      if (!Array.isArray(value)) {
        return `${filterParamString}&${queryKey}=${value}`
      }

      return value.reduce(
        (acc, val) => `${acc}&${queryKey}[]=${val}`,
        filterParamString,
      )
    },
    '',
  )
}

const sortStringFromConfig = (
  config: TableConfig,
  columnDefs: Column[],
): string => {
  const sortColumn = get(config, 'sort.columnId')
  const sortBy = get(
    lodashFind(columnDefs, { id: sortColumn }),
    'queryParamId',
    sortColumn,
  )
  const sortDirection = get(config, 'sort.direction')

  return sortBy && sortDirection
    ? `&sort_by=${sortBy}&sort_direction=${sortDirection}`
    : ''
}

const searchStringFromConfig = (config: TableConfig, columnDefs: Column[]) => {
  const searchColumn = get(config, 'search.columnId')
  const column = lodashFind(columnDefs, { id: searchColumn })
  const searchField =
    get(column, 'searchParamId') ??
    get(column, 'queryParamId') ??
    searchColumn
  const searchText = encodeURIComponent(get(config, 'search.text', ''))

  return searchField && searchText
    ? `&search_field=${searchField}&search_text=${searchText}`
    : ''
}
const paginationStringFromConfig = (config: TableConfig) => {
  const page = get(config, 'page', hookDefaults.page)
  const perPage = get(config, 'perPage', hookDefaults.perPage)

  return `&page=${page}&rows_per_page=${perPage}`
}

export const cacheKeyForAPI =
  (endpoint: string): CacheKeyGenerator =>
  (config, columnDefs) => {
    const queryParams = [
      filterStringFromConfig,
      paginationStringFromConfig,
      searchStringFromConfig,
      sortStringFromConfig,
    ]
      .map(fn => fn(config, columnDefs))
      .join('')

    return `${endpoint}?${queryParams}`
  }

export const exportKeyForApi =
  (endpoint: string) => (config: TableConfig, columnDefs: Column[]) => {
    const queryParams = [
      filterStringFromConfig,
      searchStringFromConfig,
      sortStringFromConfig,
    ]
      .map(fn => fn(config, columnDefs))
      .join('')

    return `${endpoint}?${queryParams}`
  }

export const csvFetcherForAPI =
  (endpoint: string) => (config: TableConfig, columnDefs: Column[]) =>
    arcadeApiClient.get(exportKeyForApi(endpoint)(config, columnDefs))

export const fetcherForAPI =
  (transformFn: ResultSetFromPromiseResolution<unknown>) => (url: string) => {
    return arcadeApiClient.get(url).then(res => {
      const meta = {
        count: parseInt(res.headers['x-total'], 10),
        page: parseInt(res.headers['x-page'], 10),
        perPage: parseInt(res.headers['x-per-page'], 10),
      }

      return { meta, results: transformFn(res.data) }
    })
  }
